import { useMemo } from 'react';
import { Clock, Truck, Package, Fuel, User, Activity, MapPin } from 'lucide-react';
import type { EvidenceItem } from '../../types/investigator';
import { getEvidenceCategoryLabel, getEvidenceSourceLabel } from '../../utils/evidenceDisplay';
import { humanizeInvestigationDateTimeText } from '../../utils/humanizeInvestigationDateTimeText';
import { titleCaseTimelineStopExplanation } from '../../utils/titleCasePlaceName';
import {
  buildTimelineListEntries,
  filterTimelineEvidence,
  groupEvidenceByMinute,
  resolveTimelineRouteLabel,
} from '../../utils/timelineDisplay';
import { PROVENANCE_BADGE_CLASSES } from '../../config/colors';
import { formatUkDate, formatUkTime } from '../../lib/calc';

const TYPE_ICONS: Record<string, typeof Clock> = {
  order_event: Package,
  vehicle_movement: Truck,
  driver_shift: User,
  fuel_event: Fuel,
  anomaly: Activity,
};

/** Tier badge colours come from the shared colors module (ticket 15). */
const PROVENANCE_LABEL: Record<string, string> = {
  observed: 'Confirmed',
  declared: 'Declared',
  inferred: 'Inferred',
};

interface TimelinePanelProps {
  evidence: EvidenceItem[];
  isLoading?: boolean;
  /** Full intro (subtitle + route) for standalone panels e.g. Investigator. */
  showIntroHeader?: boolean;
  /** Route with MapPin at top of panel body (Zoe: only visible when section is expanded). */
  showRouteHeader?: boolean;
}

function TimelineEventBlock({ item }: { item: EvidenceItem }) {
  const Icon = TYPE_ICONS[item.evidence_type] || Clock;
  const explanationText = titleCaseTimelineStopExplanation(
    humanizeInvestigationDateTimeText(item.explanation || ''),
  );

  return (
    <div className="border-t border-border/50 pt-2.5 first:border-t-0 first:pt-0">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{getEvidenceCategoryLabel(item)}</span>
        <span
          className={`rounded px-1.5 py-0.5 text-xs ${PROVENANCE_BADGE_CLASSES[item.provenance_kind] ?? 'bg-muted text-muted-foreground'}`}
        >
          {PROVENANCE_LABEL[item.provenance_kind] || item.provenance_kind}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-foreground">{explanationText}</p>
      {getEvidenceSourceLabel(item.source_system) ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {getEvidenceSourceLabel(item.source_system)}
        </p>
      ) : null}
    </div>
  );
}

export default function TimelinePanel({
  evidence,
  isLoading = false,
  showIntroHeader = true,
  showRouteHeader = false,
}: TimelinePanelProps) {
  const entries = useMemo(() => {
    const timelineEvidence = filterTimelineEvidence(evidence);
    const groups = groupEvidenceByMinute(timelineEvidence);
    return buildTimelineListEntries(groups);
  }, [evidence]);

  const routeLabel = useMemo(() => resolveTimelineRouteLabel(evidence), [evidence]);

  return (
    <div className="h-full overflow-y-auto pr-1">
      {showIntroHeader ? (
        <div className="mb-3">
          {isLoading ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              Events will appear here when you investigate a specific order or vehicle.
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">Events related to this investigation</p>
              {routeLabel ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                  {routeLabel}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : showRouteHeader && routeLabel && !isLoading ? (
        <p className="mb-3 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" aria-hidden />
          {routeLabel}
        </p>
      ) : null}

      {entries.length === 0 ? (
        <div className="flex items-center justify-center h-105 text-muted-foreground text-sm">
          No timeline events
        </div>
      ) : (
        <div className="relative">
          {/* Vertical spine — runs through timeline dots */}
          <div className="absolute left-3 top-2 bottom-2 w-px bg-border" aria-hidden />

          <ul className="space-y-4">
            {entries.map((entry) => {
              if (entry.kind === 'gap') {
                return (
                  <li
                    key={entry.id}
                    className="relative flex h-9 items-center"
                    aria-label={`${entry.label} between events`}
                  >
                    <span className="absolute left-12 z-20 -translate-x-1/2 whitespace-nowrap rounded bg-background px-2 py-1 text-sm font-semibold text-muted-foreground">
                      {entry.label}
                    </span>
                  </li>
                );
              }

              const { group } = entry;
              const ts = new Date(group.occurred_at);
              const time = formatUkTime(ts, {}, '');
              const date = formatUkDate(ts, '');
              return (
                <li key={group.minuteKey} className="relative flex gap-3 items-stretch group">
                  {/* Dot on spine — glows when the row (event card) is hovered */}
                  <div
                    className="absolute left-1.5 top-1/2 z-10 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-accent bg-secondary transition-all duration-200 group-hover:border-success group-hover:bg-success group-hover:shadow-glow-success-lg"
                    aria-hidden
                  />

                  {/* Time column — plain text, no container */}
                  <div
                    className="ml-6 w-20 shrink-0 flex flex-col justify-center py-0.5"
                    aria-label={`${date} ${time}`}
                  >
                    <span className="text-sm font-semibold leading-tight text-foreground">{date}</span>
                    <span className="mt-0.5 text-base font-mono font-semibold tabular-nums leading-none text-foreground">
                      {time}
                    </span>
                    {group.items.length > 1 ? (
                      <span className="mt-1 text-xs font-medium text-muted-foreground">
                        {group.items.length} events
                      </span>
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1 rounded-lg border border-border/50 bg-card/60 p-3 transition-all duration-200 group-hover:border-success/55 group-hover:shadow-glow-success-lg">
                    <div className="space-y-2.5">
                      {group.items.map((item, i) => (
                        <TimelineEventBlock
                          key={item.evidence_id || `${group.minuteKey}-${i}`}
                          item={item}
                        />
                      ))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
